import { format, addMinutes, parse, isAfter, isBefore, addDays } from 'date-fns';
import { Appointment, Service, BlockedTime, TimeSlot, SalonSettings, TeamMember, DEFAULT_SALON_SETTINGS } from '../types';

// Format date to display format
export const formatDisplayDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'MMMM d, yyyy');
};

// Format time to display format (12-hour)
export const formatDisplayTime = (time: string): string => {
  // Convert 24h format (14:30) to Date object
  const date = parse(time, 'HH:mm', new Date());
  return format(date, 'h:mm a');
};

// Convert time string (14:30) to minutes since midnight
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

// Convert minutes since midnight to time string (14:30)
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Calculate end time based on start time and duration
export const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const startDate = parse(startTime, 'HH:mm', new Date());
  const endDate = addMinutes(startDate, durationMinutes);
  return format(endDate, 'HH:mm');
};

// Calculate total duration including cleanup time
export const calculateTotalDuration = (serviceDuration: number): number => {
  // Add 30 minutes for cleanup/preparation
  return serviceDuration + 30;
};

const CLEANUP_BUFFER_MS = 30 * 60 * 1000; // 30 minutes in ms

// Check if a time slot overlaps with an appointment (including cleanup buffer after each appointment)
export const isOverlapping = (
  date: string,
  startTime: string,
  endTime: string,
  appointments: Appointment[]
): boolean => {
  const slotStart = new Date(`${date}T${startTime}`);
  const slotEnd = new Date(`${date}T${endTime}`);

  return appointments.some(appointment => {
    if (appointment.date !== date) return false;
    if ((appointment as any).status === 'cancelled') return false;

    const appointmentStart = new Date(`${appointment.date}T${appointment.startTime}`);
    const appointmentServiceEnd = new Date(`${appointment.date}T${appointment.endTime}`);
    // The appointment occupies until service end + 30min cleanup
    const appointmentOccupiedEnd = new Date(appointmentServiceEnd.getTime() + CLEANUP_BUFFER_MS);

    // New slot overlaps if it starts or ends inside the occupied window,
    // or fully wraps around it
    return (
      slotStart.getTime() === appointmentStart.getTime() ||
      (isAfter(slotStart, appointmentStart) && isBefore(slotStart, appointmentOccupiedEnd)) ||
      (isAfter(slotEnd, appointmentStart) && isBefore(slotEnd, appointmentOccupiedEnd)) ||
      (isBefore(slotStart, appointmentStart) && isAfter(slotEnd, appointmentStart))
    );
  });
};

// Check if a time slot is within blocked times
export const isBlocked = (
  date: string,
  startTime: string,
  endTime: string,
  blockedTimes: BlockedTime[],
  teamMember?: TeamMember | null
): boolean => {
  const slotStart = new Date(`${date}T${startTime}`);
  const slotEnd = new Date(`${date}T${endTime}`);

  return blockedTimes.some(blockedTime => {
    // Check if the blocked time is on the same date
    if (blockedTime.date !== date) {
      return false;
    }

    // If a team member is provided, only apply blocks that are global or for that member
    if (teamMember) {
      if (blockedTime.teamMemberId && blockedTime.teamMemberId !== teamMember.id) {
        return false;
      }
    }
    
    const blockStart = new Date(`${date}T${blockedTime.startTime}`);
    const blockEnd = new Date(`${date}T${blockedTime.endTime}`);
    
    // Check if there's any overlap
    return (
      (isAfter(slotStart, blockStart) && isBefore(slotStart, blockEnd)) ||
      (isAfter(slotEnd, blockStart) && isBefore(slotEnd, blockEnd)) ||
      (isBefore(slotStart, blockStart) && isAfter(slotEnd, blockEnd)) ||
      slotStart.getTime() === blockStart.getTime() ||
      slotEnd.getTime() === blockEnd.getTime()
    );
  });
};

// Check if a date is a working day based on salon settings (and optionally a team member's working days)
export const isWorkingDay = (
  date: string,
  settings: SalonSettings = DEFAULT_SALON_SETTINGS,
  teamMember?: TeamMember | null
): boolean => {
  const dateObj = new Date(date + 'T12:00:00');
  const day = dateObj.getDay();
  // Check holiday first
  if (settings.holidays.includes(date)) return false;
  // If a team member is provided, check their working days
  if (teamMember) {
    return teamMember.workingDays.includes(day) && (settings.businessHours[day]?.isOpen ?? false);
  }
  return settings.businessHours[day]?.isOpen ?? false;
};

// Generate available time slots for a specific date
export const generateTimeSlots = (
  date: string,
  service: Service,
  appointments: Appointment[],
  blockedTimes: BlockedTime[],
  settings: SalonSettings = DEFAULT_SALON_SETTINGS,
  teamMember?: TeamMember | null
): TimeSlot[] => {
  const slots: TimeSlot[] = [];

  if (!isWorkingDay(date, settings, teamMember)) return slots;

  // If a specific team member is selected, only consider their appointments
  const relevantAppointments = teamMember
    ? appointments.filter(a => a.teamMemberId === teamMember.id)
    : appointments;

  const dateObj = new Date(date + 'T12:00:00');
  const day = dateObj.getDay();
  const salonDaySchedule = settings.businessHours[day];
  const memberDaySchedule = teamMember?.workingHours?.[day];

  // Member can start no earlier than salon opens, and finish no later than salon closes
  const salonOpen = timeToMinutes(salonDaySchedule.openTime);
  const salonClose = timeToMinutes(salonDaySchedule.closeTime);
  const startMinutes = memberDaySchedule
    ? Math.max(timeToMinutes(memberDaySchedule.openTime), salonOpen)
    : salonOpen;
  const endMinutes = memberDaySchedule
    ? Math.min(timeToMinutes(memberDaySchedule.closeTime), salonClose)
    : salonClose;

  const serviceDuration = service.duration;
  const totalDuration = calculateTotalDuration(serviceDuration);

  // The service must END by closing time, with a minimum 1h buffer before closing.
  const minBufferBeforeClose = 60;
  const lastSlotMinutes = endMinutes - Math.max(minBufferBeforeClose, serviceDuration);

  // For today, reject slots whose start time has already passed (+ 30 min buffer)
  const now = new Date();
  const todayStr = format(now, 'yyyy-MM-dd');
  const isToday = date === todayStr;
  const nowMinutes = isToday ? now.getHours() * 60 + now.getMinutes() + 30 : 0;

  for (let time = startMinutes; time <= lastSlotMinutes; time += 30) {
    if (isToday && time <= nowMinutes) continue;

    const startTime = minutesToTime(time);
    const endTime = calculateEndTime(startTime, serviceDuration);
    const cleanupEndTime = calculateEndTime(startTime, totalDuration);

    const available =
      !isOverlapping(date, startTime, cleanupEndTime, relevantAppointments) &&
      !isBlocked(date, startTime, cleanupEndTime, blockedTimes, teamMember);

    slots.push({ date, startTime, endTime, available });
  }

  return slots;
};

// Find the next available date for a team member before a given deadline date
// Returns the date string or null if none found
export const findNextAvailableDate = (
  member: TeamMember,
  service: Service,
  appointments: Appointment[],
  blockedTimes: BlockedTime[],
  settings: SalonSettings,
  beforeDate: Date, // only look for dates strictly before this
  maxDaysAhead = 90
): string | null => {
  const today = new Date();
  for (let i = 0; i < maxDaysAhead; i++) {
    const candidate = addDays(today, i);
    if (candidate >= beforeDate) break; // stop once we reach or pass the selected date
    const dateStr = format(candidate, 'yyyy-MM-dd');
    const slots = generateTimeSlots(dateStr, service, appointments, blockedTimes, settings, member);
    if (slots.some(s => s.available)) return dateStr;
  }
  return null;
};

// Generate dates for the next N days
export const generateDatesForRange = (days: number): string[] => {
  const dates: string[] = [];
  const today = new Date();
  
  for (let i = 0; i < days; i++) {
    const date = addDays(today, i);
    dates.push(format(date, 'yyyy-MM-dd'));
  }
  
  return dates;
};
